import { useCallback, useEffect, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	ChartOptions,
	Filler,
	Legend,
	LineElement,
	LinearScale,
	PointElement,
	Tooltip
} from 'chart.js';
import {
	Box,
	Button,
	Flex,
	Heading,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useToast
} from '@chakra-ui/react';

import { GranularidadeGrafico, GraficosDashResponse, graficosDash } from '../../services/api';
import { fimDoDiaISO, inicioDoDiaISO, paraDataLocal } from '../../utils/data';
import { Loading } from '../Loading';
import { rotuloStatusAula } from '../StatusAula';

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	LineElement,
	PointElement,
	Filler,
	Tooltip,
	Legend
);

// As duas séries saem do tema: royal para o dado principal, brass para o
// contraste. É o passo 600 do brass, e não o 500 da marca, porque o 500 fica em
// 2.59:1 contra o branco - abaixo do mínimo para uma área colorida carregar
// significado sozinha.
const ROYAL = '#1E56C8';
const BRASS = '#a97f33';
const ROYAL_LAVADO = 'rgba(30, 86, 200, 0.10)';
const MIST = '#E4EAF3';
const CINZA_ROTULO = '#6B7A99';

type Periodo = 7 | 30 | 90;

const PERIODOS: { dias: Periodo; rotulo: string }[] = [
	{ dias: 7, rotulo: '7 dias' },
	{ dias: 30, rotulo: '30 dias' },
	{ dias: 90, rotulo: '90 dias' }
];

// Noventa dias em baldes diários seriam noventa colunas ilegíveis; a partir daí
// a semana é a menor unidade que ainda cabe na largura do cartão.
const granularidadeDe = (dias: Periodo): GranularidadeGrafico => (dias > 30 ? 'semana' : 'dia');

const MESES = [ 'jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez' ];

/** `2026-08-24` vira `24 ago`; `2026-08` vira `ago/26`. */
const formatarBucket = (bucket: string): string => {
	const partes = bucket.split('-');
	if (partes.length === 2) return `${MESES[Number(partes[1]) - 1]}/${partes[0].slice(2)}`;
	return `${Number(partes[2])} ${MESES[Number(partes[1]) - 1]}`;
};

const janelaDe = (dias: Periodo) => {
	const hoje = new Date();
	const inicio = new Date(hoje);
	inicio.setDate(inicio.getDate() - (dias - 1));
	return {
		de: inicioDoDiaISO(paraDataLocal(inicio.toISOString())) as string,
		ate: fimDoDiaISO(paraDataLocal(hoje.toISOString())) as string
	};
};

// A grade pertence ao eixo de VALOR, nunca ao de categoria: linha entre
// categorias não ajuda a ler magnitude, só polui. Na barra horizontal os papéis
// se invertem, e é por isso que a orientação é parâmetro.
const eixoDeCategoria = {
	grid: { display: false },
	border: { color: MIST },
	ticks: { color: CINZA_ROTULO, font: { size: 11 } }
};

const eixoDeValor = {
	beginAtZero: true,
	// Filete sólido e recuado: nunca tracejado, que lê como projeção ou limite
	// quando é só grade.
	grid: { color: MIST },
	border: { display: false },
	ticks: { color: CINZA_ROTULO, font: { size: 11 }, precision: 0 }
};

const eixos = (
	{ horizontal = false, empilhado = false } = {}
): ChartOptions<'bar' | 'line'>['scales'] => ({
	x: { ...(horizontal ? eixoDeValor : eixoDeCategoria), stacked: empilhado },
	y: { ...(horizontal ? eixoDeCategoria : eixoDeValor), stacked: empilhado }
});

const SEM_LEGENDA = { legend: { display: false as const } };

type CardGraficoProps = {
	titulo: string;
	pergunta: string;
	colunas: string[];
	linhas: (string | number)[][];
	children: React.ReactNode;
};

/**
 * Moldura do gráfico com a tabela equivalente embutida. A tabela não é extra: o
 * canvas do Chart.js não é legível por leitor de tela, e o valor precisa estar
 * disponível sem depender de passar o mouse.
 */
const CardGrafico = ({ titulo, pergunta, colunas, linhas, children }: CardGraficoProps) => {
	const [ mostrarTabela, setMostrarTabela ] = useState(false);

	return (
		<Flex
			direction="column"
			gap="14px"
			bg="white"
			border="1px solid"
			borderColor="mist"
			borderRadius="14px"
			boxShadow="sm"
			p="18px"
			flex="1"
			minW={{ base: '100%', lg: '320px' }}
		>
			<Box>
				<Heading as="h3" fontSize="15px" fontWeight="700" color="ink">
					{titulo}
				</Heading>
				<Text fontSize="13px" color="gray.600">
					{pergunta}
				</Text>
			</Box>

			<Box height="220px">{children}</Box>

			<Flex justify="flex-end" borderTop="1px solid" borderColor="mist" pt="10px">
				<Button
					variant="link"
					size="sm"
					colorScheme="blue"
					fontSize="12px"
					onClick={() => setMostrarTabela((atual) => !atual)}
					aria-expanded={mostrarTabela}
				>
					{mostrarTabela ? 'Ocultar tabela' : 'Ver tabela'}
				</Button>
			</Flex>

			{mostrarTabela && (
				<Box maxH="220px" overflowY="auto">
					<Table size="sm" variant="simple" sx={{ fontVariantNumeric: 'tabular-nums' }}>
						<Thead>
							<Tr>
								{colunas.map((coluna) => (
									<Th key={coluna}>{coluna}</Th>
								))}
							</Tr>
						</Thead>
						<Tbody>
							{linhas.map((linha) => (
								<Tr key={String(linha[0])}>
									{linha.map((celula, indice) => (
										<Td key={indice}>{celula}</Td>
									))}
								</Tr>
							))}
						</Tbody>
					</Table>
				</Box>
			)}
		</Flex>
	);
};

export const GraficosDashboard = () => {
	const [ periodo, setPeriodo ] = useState<Periodo>(30);
	const [ dados, setDados ] = useState<GraficosDashResponse | null>(null);
	const [ carregando, setCarregando ] = useState(true);
	const toast = useToast();

	const carregar = useCallback(
		(dias: Periodo) => {
			const { de, ate } = janelaDe(dias);
			setCarregando(true);
			// Os dois estados mudam no mesmo callback de propósito: em `.finally`
			// separado eles caem em microtasks diferentes, viram duas renderizações
			// por carregamento e o React reclama de update fora do act() no teste.
			graficosDash(de, ate, granularidadeDe(dias))
				.then((res) => {
					setDados(res.data);
					setCarregando(false);
				})
				.catch(() => {
					setCarregando(false);
					toast({
						title: 'Não foi possível carregar os gráficos.',
						status: 'error',
						isClosable: true
					});
				});
		},
		[ toast ]
	);

	useEffect(() => {
		carregar(periodo);
	}, [ periodo, carregar ]);

	if (carregando && !dados) return <Loading label="Carregando gráficos..." />;
	if (!dados) return null;

	const rotulosPeriodo = dados.aulasPorPeriodo.map((p) => formatarBucket(p.bucket));
	const rotulosEncerramento = dados.encerramentoPorTipo.map((e) => formatarBucket(e.bucket));

	return (
		<Flex direction="column" gap="16px" as="section" aria-label="Gráficos do painel">
			{/* Um filtro só, acima de tudo que ele governa: se cada cartão tivesse o
			    seu, dois gráficos lado a lado poderiam mostrar recortes diferentes e
			    discordar sobre a mesma escola. */}
			<Flex align="center" gap="10px" flexWrap="wrap">
				<Text fontSize="12px" fontWeight="700" letterSpacing="0.08em" textTransform="uppercase" color="gray.500">
					Período
				</Text>
				<Flex gap="6px">
					{PERIODOS.map(({ dias, rotulo }) => (
						<Button
							key={dias}
							size="sm"
							fontSize="13px"
							variant={periodo === dias ? 'solid' : 'ghost'}
							colorScheme="blue"
							aria-pressed={periodo === dias}
							onClick={() => setPeriodo(dias)}
						>
							{rotulo}
						</Button>
					))}
				</Flex>
			</Flex>

			{/* Enquanto recarrega, a renderização anterior fica em opacidade reduzida:
			    sem esqueleto e sem salto de layout. */}
			<Flex direction="column" gap="16px" opacity={carregando ? 0.5 : 1} transition="opacity 120ms">
				<CardGrafico
					titulo="Aulas por dia"
					pergunta="O movimento da escola subiu ou caiu no período?"
					colunas={[ 'Período', 'Aulas' ]}
					linhas={dados.aulasPorPeriodo.map((p) => [ p.bucket, p.total ])}
				>
					<Line
						aria-label="Aulas por dia no período selecionado"
						role="img"
						data={{
							labels: rotulosPeriodo,
							datasets: [
								{
									label: 'Aulas',
									data: dados.aulasPorPeriodo.map((p) => p.total),
									borderColor: ROYAL,
									backgroundColor: ROYAL_LAVADO,
									borderWidth: 2,
									fill: true,
									pointRadius: 0,
									pointHoverRadius: 5,
									pointHoverBorderWidth: 2,
									pointHoverBorderColor: '#ffffff',
									pointHoverBackgroundColor: ROYAL
								}
							]
						}}
						options={{
							maintainAspectRatio: false,
							// O leitor mira uma data, nunca uma linha de 2px.
							interaction: { mode: 'index', intersect: false },
							plugins: SEM_LEGENDA,
							scales: eixos()
						}}
					/>
				</CardGrafico>

				<Flex gap="16px" direction={{ base: 'column', lg: 'row' }}>
					<CardGrafico
						titulo="Aulas por status"
						pergunta="Quanto do que foi marcado virou aula dada?"
						colunas={[ 'Status', 'Aulas' ]}
						linhas={dados.aulasPorStatus.map((s) => [ rotuloStatusAula(s.status), s.total ])}
					>
						<Bar
							aria-label="Aulas por status"
							role="img"
							data={{
								labels: dados.aulasPorStatus.map((s) => rotuloStatusAula(s.status)),
								datasets: [
									{
										label: 'Aulas',
										data: dados.aulasPorStatus.map((s) => s.total),
										backgroundColor: ROYAL,
										borderRadius: 4,
										maxBarThickness: 24
									}
								]
							}}
							options={{
								indexAxis: 'y',
								maintainAspectRatio: false,
								plugins: SEM_LEGENDA,
								scales: eixos({ horizontal: true })
							}}
						/>
					</CardGrafico>

					<CardGrafico
						titulo="Aulas concluídas por professor"
						pergunta="A carga está equilibrada entre a equipe?"
						colunas={[ 'Professor', 'Aulas' ]}
						linhas={dados.aulasPorProfessor.map((p) => [ p.nome, p.total ])}
					>
						<Bar
							aria-label="Aulas concluídas por professor"
							role="img"
							data={{
								labels: dados.aulasPorProfessor.map((p) => p.nome),
								datasets: [
									{
										label: 'Aulas concluídas',
										data: dados.aulasPorProfessor.map((p) => p.total),
										backgroundColor: ROYAL,
										borderRadius: 4,
										maxBarThickness: 24
									}
								]
							}}
							options={{
								indexAxis: 'y',
								maintainAspectRatio: false,
								plugins: SEM_LEGENDA,
								scales: eixos({ horizontal: true })
							}}
						/>
					</CardGrafico>
				</Flex>

				<CardGrafico
					titulo="Como a aula foi encerrada"
					pergunta="Quantas aulas o professor esqueceu de fechar e o cron fechou por ele?"
					colunas={[ 'Período', 'Manual', 'Via cron' ]}
					linhas={dados.encerramentoPorTipo.map((e) => [ e.bucket, e.manual, e.automatica ])}
				>
					<Bar
						aria-label="Aulas concluídas, separadas entre encerramento manual e automático"
						role="img"
						data={{
							labels: rotulosEncerramento,
							datasets: [
								{
									label: 'Encerrada pelo professor',
									data: dados.encerramentoPorTipo.map((e) => e.manual),
									backgroundColor: ROYAL,
									maxBarThickness: 24
								},
								{
									label: 'Encerrada pelo cron',
									data: dados.encerramentoPorTipo.map((e) => e.automatica),
									backgroundColor: BRASS,
									borderRadius: 4,
									maxBarThickness: 24,
									// Quem separa as duas fatias é um filete da cor do fundo, e não
									// um contorno em volta delas: contorno adiciona tinta que não é
									// dado.
									borderColor: '#ffffff',
									borderWidth: { bottom: 2, top: 0, left: 0, right: 0 },
									borderSkipped: false
								}
							]
						}}
						options={{
							maintainAspectRatio: false,
							interaction: { mode: 'index', intersect: false },
							// Duas séries: a legenda é obrigatória, porque identidade nunca
							// pode depender só da cor.
							plugins: {
								legend: {
									display: true,
									position: 'top',
									align: 'start',
									labels: { boxWidth: 11, boxHeight: 11, color: CINZA_ROTULO, font: { size: 12 } }
								}
							},
							scales: eixos({ empilhado: true })
						}}
					/>
				</CardGrafico>
			</Flex>
		</Flex>
	);
};
